import { AxiosError, AxiosRequestConfig, AxiosResponse, AxiosHeaders } from 'axios';
import { CapacitorHttp } from "@capacitor/core";

const fetchFallback = async (config: AxiosRequestConfig, headers: Record<string, string>, fullUrl: string): Promise<AxiosResponse> => {
    let url = fullUrl;
    if (config.params) {
        const params = new URLSearchParams(config.params);
        url = `${url}?${params.toString()}`;
    }

    const method = config.method?.toUpperCase() || 'GET';
    const hasBody = !['GET', 'HEAD'].includes(method);
    const body = hasBody && config.data
        ? (typeof config.data === 'string' ? config.data : JSON.stringify(config.data))
        : undefined;

    const fetchResponse = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', ...headers },
        body,
    });

    let data: any;
    const contentType = fetchResponse.headers.get('content-type') || '';
    if (config.responseType === 'arraybuffer') {
        data = await fetchResponse.arrayBuffer();
    } else if (config.responseType === 'blob') {
        data = await fetchResponse.blob();
    } else if (contentType.includes('application/json')) {
        data = await fetchResponse.json();
    } else {
        data = await fetchResponse.text();
    }

    const responseHeaders: Record<string, string> = {};
    fetchResponse.headers.forEach((value, key) => { responseHeaders[key] = value; });

    const res: AxiosResponse = {
        data,
        status: fetchResponse.status,
        statusText: fetchResponse.statusText,
        headers: responseHeaders,
        config: { ...config, headers: AxiosHeaders.from(headers) },
        request: undefined,
    };

    if (!fetchResponse.ok) {
        throw new AxiosError(
            fetchResponse.statusText,
            fetchResponse.status + "",
            { ...config, headers: AxiosHeaders.from(headers) },
            undefined,
            res
        );
    }

    return res;
};

export const capacitorAxiosAdapter = async (config: AxiosRequestConfig): Promise<AxiosResponse> => {
    const method = config.method?.toUpperCase() || 'GET';

    // Asegura que los headers sean un objeto plano y nunca undefined
    let headers: Record<string, string> = {};
    if (config.headers) {
        if (config.headers instanceof AxiosHeaders) {
            const rawHeaders = config.headers.toJSON();
            headers = Object.fromEntries(
                Object.entries(rawHeaders)
                    .filter(([_, v]) => v != null)
                    .map(([k, v]) => [k, String(v)])
            );
        } else {
            headers = { ...config.headers } as Record<string, string>;
        }
    }

    const options: any = {
        url: (config.baseURL || '') + config.url,
        headers,
        params: config.params,
        data: config.data,
        method: method
    };

    try {
        const response = await CapacitorHttp.request(options);

        const res: AxiosResponse = {
            data: response.data,
            status: response.status,
            statusText: '', // HttpResponse does not have statusText
            headers: response.headers || {},
            config: { ...config, headers: AxiosHeaders.from(headers) }, // headers nunca será undefined
            request: options.request,
        };

        if (response.status > 299 || response.status < 200)
            throw new AxiosError(
                "",
                response.status + "",
                { ...config, headers: AxiosHeaders.from(headers) },
                options.request,
                res
            );

        return res;
    } catch (error: any) {
        if (error?.message?.includes('not implemented')) {
            return fetchFallback(config, headers, options.url);
        }
        throw error;
    }
};